import React, { useContext, useState, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import i18n from 'i18n';
import { sendOTP, sendRestorePasswordOTP } from 'actions/profile';
import useBackIf from 'hooks/useBackIf';
import { RestorePasswordContext } from './RestorePasswordContext';
import Header from '../common/Header';
import OTPForm from '../common/OTPForm';
import ActionsSection from '../ActionsSection';
import { StyledWhiteText } from '../StyledComponents';

const i18nScope = 'authorizationRestorePasswordOTPPage';
const SECTION_NAME = 'OTP_INPUT';
const RESEND_CODE_THRESHOLD_SECONDS = 59;
const MAX_OTP_ATTEMPS = 10;

const getDescription = ({ code, phone, email }) => {
  const loginValue = email || code + phone;
  const descriptionText = email ? 'descriptionEmail' : 'description';
  return [
    <StyledWhiteText key="login-value">{loginValue}</StyledWhiteText>,
    <br key="br" />,
    i18n.t(`${i18nScope}.${descriptionText}`),
  ];
};

const AuthorizationRestorePasswordOTPPage = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { contextState, setContextState } = useContext(RestorePasswordContext);
  const { code, phone, email } = contextState;
  const search = new URLSearchParams(history.location.search);
  const contractId = search.get('contractId');
  const contractNumber = search.get('contractNumber');
  const [timeToNextResend, setTimeToNextResend] = useState(RESEND_CODE_THRESHOLD_SECONDS);
  const [attemps, setAttemps] = useState(0);

  const updateTimeToNextResend = useCallback(
    () => setTimeToNextResend((value) => value && value - 1),
    [setTimeToNextResend],
  );

  const onSubmit = useCallback(
    (otp) => {
      let currentAttemptNumber = attemps + 1;
      setContextState((state) => ({ ...state, otp, contractNumber, contractId }));
      dispatch(
        sendOTP({
          otp,
          email,
          phone,
          contractId,
          errorModal: { returnFocusTo: `@${SECTION_NAME}` },
        }),
      );

      if (currentAttemptNumber >= MAX_OTP_ATTEMPS) {
        setTimeToNextResend(0);
        currentAttemptNumber = 0;
      }

      setAttemps(currentAttemptNumber);
    },
    [attemps, setContextState, dispatch, email, phone, contractId, contractNumber],
  );

  const onResend = useCallback(() => {
    dispatch(
      sendRestorePasswordOTP({
        email,
        phone,
        contractId,
        errorModal: { returnFocusTo: `@${SECTION_NAME}` },
      }),
    );
    setTimeToNextResend(RESEND_CODE_THRESHOLD_SECONDS);
  }, [dispatch, email, phone, contractId]);

  useEffect(() => {
    const intervalId = setInterval(updateTimeToNextResend, 1000);
    return () => clearInterval(intervalId);
  });

  useBackIf(!phone && !email);

  return (
    <>
      <Header title={i18n.t(`${i18nScope}.title`)} description={getDescription({ code, phone, email })} />
      <OTPForm
        sectionName={SECTION_NAME}
        buttonLabel={i18n.t(`${i18nScope}.next`)}
        invalidMessage={i18n.t(`${i18nScope}.otpInvalid`)}
        onSubmit={onSubmit}
      >
        {({ resetInput }) => {
          return (
            <ActionsSection.Button
              label={i18n.t(`${i18nScope}.${timeToNextResend <= 0 ? 'resendCode' : 'resendCodeTimer'}`, {
                seconds: `0${timeToNextResend}`.slice(-2),
              })}
              onEnter={() => {
                onResend();
                resetInput();
              }}
              disabled={timeToNextResend > 0}
            />
          );
        }}
      </OTPForm>
    </>
  );
};

export default AuthorizationRestorePasswordOTPPage;
